import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Users, ShieldCheck, Trash2, RotateCcw, CheckCircle2,
  Crown, UserCog, Radio,
} from "lucide-react";
import { api } from "../lib/api";
import { Skeleton } from "../components/ui/skeleton";
import { LinkedKiteControls } from "../components/LinkedKiteControls";

const ROLES = ["USER", "POWER_USER", "ADMIN"];
const SUBS = ["NONE", "TIPS", "AUTO_TRADING"];

const Pill = ({ children, color = "muted" }) => (
  <span
    className="inline-block px-2 py-0.5 text-[10px] font-mono-data uppercase tracking-wider border"
    style={{ color: `var(--${color})`, borderColor: `var(--${color})` }}
  >
    {children}
  </span>
);

export const AdminUsers = () => {
  const [users, setUsers] = useState(null);
  const [includeDeleted, setIncludeDeleted] = useState(false);
  // 2026-09-13 — chat: "User record should be added to table after Profile
  // Setup (user should accept T&C and others)". Off by default — hides
  // bare/abandoned sign-ins that haven't finished Profile Setup and/or
  // haven't accepted the current legal documents yet (see
  // admin_list_users' docstring for why the row still exists in Mongo
  // regardless). Mirrors includeDeleted's own toggle pattern.
  const [includeIncomplete, setIncludeIncomplete] = useState(false);
  const [filter, setFilter] = useState("");

  // 2026-10-15 — linked Zerodha accounts (copy settings moved here from the
  // Live Orders console): account_id -> kite account, and which row is open.
  const [kiteAccs, setKiteAccs] = useState({});
  const [openKite, setOpenKite] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(
        `/admin/users?include_deleted=${includeDeleted}&include_incomplete=${includeIncomplete}`
      );
      setUsers(data.users || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load users");
    }
    try {
      const { data } = await api.get("/kite-accounts");
      const m = {};
      (data.accounts || []).filter((a) => !a.primary).forEach((a) => { m[a.account_id] = a; });
      setKiteAccs(m);
    } catch (e) { /* console not available — leave the column empty */ }
  }, [includeDeleted, includeIncomplete]);

  useEffect(() => { load(); }, [load]);

  const approve = async (u) => {
    await api.post(`/admin/users/${u.user_id}/approve`);
    toast.success(`${u.email} approved`);
    load();
  };
  const setRole = async (u, role) => {
    await api.post(`/admin/users/${u.user_id}/role`, { role });
    toast.success(`${u.email} → ${role}`);
    load();
  };
  const setSub = async (u, subscription) => {
    await api.post(`/admin/users/${u.user_id}/subscription`, { subscription });
    toast.success(`${u.email} subscription → ${subscription}`);
    load();
  };
  const toggleLive = async (u) => {
    await api.post(`/admin/users/${u.user_id}/live-trading`, { enabled: !u.live_trading_enabled });
    toast.success(`Live trading ${!u.live_trading_enabled ? "enabled" : "disabled"} for ${u.email}`);
    load();
  };
  const toggleCommodityLive = async (u) => {
    await api.post(`/admin/users/${u.user_id}/commodity-live-trading`,
      { enabled: !u.commodity_live_trading_enabled });
    toast.success(`Commodity live trading ${!u.commodity_live_trading_enabled ? "enabled" : "disabled"} for ${u.email}`);
    load();
  };
  const softDelete = async (u) => {
    if (!window.confirm(`Soft-delete ${u.email}? Active sessions terminate, trading stops.`)) return;
    await api.post(`/admin/users/${u.user_id}/soft-delete`);
    toast.success(`${u.email} deactivated`);
    load();
  };
  const restore = async (u) => {
    await api.post(`/admin/users/${u.user_id}/restore`);
    toast.success(`${u.email} restored`);
    load();
  };

  const filtered = (users || []).filter((u) =>
    !filter ||
    u.email?.toLowerCase().includes(filter.toLowerCase()) ||
    u.name?.toLowerCase().includes(filter.toLowerCase())
  );

  return (
    <section className="space-y-4" data-testid="admin-users-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-muted-3 font-mono-data mb-1">
            Admin · Manage Users
          </div>
          <h2 className="font-heading text-2xl flex items-center gap-2"><Users size={20} /> Users</h2>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="text"
            placeholder="Search email or name..."
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            data-testid="admin-users-search"
            className="bg-transparent border border-grid px-3 py-1.5 text-xs"
          />
          <label className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] font-mono-data text-muted-2">
            <input
              type="checkbox"
              checked={includeDeleted}
              onChange={(e) => setIncludeDeleted(e.target.checked)}
              data-testid="admin-users-include-deleted"
            />
            Show deleted
          </label>
          <label className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] font-mono-data text-muted-2">
            <input
              type="checkbox"
              checked={includeIncomplete}
              onChange={(e) => setIncludeIncomplete(e.target.checked)}
              data-testid="admin-users-include-incomplete"
            />
            Show incomplete sign-ins
          </label>
        </div>
      </div>

      {users === null ? (
        <Skeleton className="h-32 w-full" />
      ) : filtered.length === 0 ? (
        <div className="border border-grid p-6 text-center text-xs text-muted-3 font-mono-data">
          No users.
        </div>
      ) : (
        <div className="border border-grid overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-grid text-[10px] uppercase tracking-[0.2em] text-muted-3 font-mono-data">
                <th className="px-3 py-2 text-left">User</th>
                <th className="px-3 py-2 text-left">Role</th>
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2 text-left">Subscription</th>
                <th className="px-3 py-2 text-left">Live</th>
                <th className="px-3 py-2 text-left">Commodity Live</th>
                <th className="px-3 py-2 text-left">Zerodha copy</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((u, i) => (
                <React.Fragment key={u.user_id}>
                <tr className="border-b border-grid hover:bg-white/[0.02]"
                    data-testid={`admin-user-row-${i}`}>
                  <td className="px-3 py-2">
                    <div className="font-semibold">{u.name || u.email}</div>
                    <div className="text-[10px] text-muted-3 font-mono-data">{u.email}</div>
                    {(!u.profile_complete || !u.legal_accepted) && (
                      <div className="flex gap-1 mt-1">
                        {!u.profile_complete && <Pill color="muted-3">PROFILE INCOMPLETE</Pill>}
                        {u.profile_complete && !u.legal_accepted && <Pill color="muted-3">T&amp;C PENDING</Pill>}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <select
                      value={u.role}
                      onChange={(e) => setRole(u, e.target.value)}
                      data-testid={`admin-user-role-${i}`}
                      className="bg-transparent border border-grid px-2 py-1 text-xs"
                    >
                      {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                    </select>
                  </td>
                  <td className="px-3 py-2">
                    {u.status === "PENDING" && <Pill color="muted-3">PENDING</Pill>}
                    {u.status === "ACTIVE" && <Pill color="bull">ACTIVE</Pill>}
                    {u.status === "SOFT_DELETED" && <Pill color="bear">DELETED</Pill>}
                  </td>
                  <td className="px-3 py-2">
                    <select
                      value={u.subscription}
                      onChange={(e) => setSub(u, e.target.value)}
                      data-testid={`admin-user-sub-${i}`}
                      className="bg-transparent border border-grid px-2 py-1 text-xs"
                    >
                      {SUBS.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </td>
                  <td className="px-3 py-2">
                    <button
                      onClick={() => toggleLive(u)}
                      data-testid={`admin-user-live-${i}`}
                      className="text-[10px] font-mono-data uppercase tracking-wider px-2 py-1 border"
                      style={{
                        color: u.live_trading_enabled ? "var(--bull)" : "var(--muted-3)",
                        borderColor: u.live_trading_enabled ? "var(--bull)" : "var(--grid)",
                      }}
                    >
                      {u.live_trading_enabled ? "ON" : "OFF"}
                    </button>
                  </td>
                  <td className="px-3 py-2">
                    <button
                      onClick={() => toggleCommodityLive(u)}
                      data-testid={`admin-user-commodity-live-${i}`}
                      className="text-[10px] font-mono-data uppercase tracking-wider px-2 py-1 border"
                      style={{
                        color: u.commodity_live_trading_enabled ? "var(--bull)" : "var(--muted-3)",
                        borderColor: u.commodity_live_trading_enabled ? "var(--bull)" : "var(--grid)",
                      }}
                    >
                      {u.commodity_live_trading_enabled ? "ON" : "OFF"}
                    </button>
                  </td>
                  <td className="px-3 py-2">
                    {kiteAccs[u.user_id] ? (
                      <button
                        onClick={() => setOpenKite(openKite === u.user_id ? null : u.user_id)}
                        data-testid={`admin-user-kite-${i}`}
                        className="text-[10px] font-mono-data uppercase tracking-wider px-2 py-1 border"
                        style={{
                          color: kiteAccs[u.user_id].enabled ? "var(--bull)" : "var(--muted-3)",
                          borderColor: kiteAccs[u.user_id].enabled ? "var(--bull)" : "var(--grid)",
                        }}
                        title="Copy settings for this user's Zerodha account"
                      >
                        {kiteAccs[u.user_id].enabled
                          ? (kiteAccs[u.user_id].live_enabled ? "LIVE" : "DRY RUN") : "OFF"} {openKite === u.user_id ? "▴" : "▾"}
                      </button>
                    ) : <span className="text-[10px] text-muted-3 font-mono-data">—</span>}
                  </td>
                  <td className="px-3 py-2 text-right space-x-1">
                    {u.status === "PENDING" && (
                      <button onClick={() => approve(u)}
                              data-testid={`admin-user-approve-${i}`}
                              title="Approve"
                              className="p-1.5 border border-grid hover:bg-white/[0.04]"
                              style={{ color: "var(--bull)" }}>
                        <CheckCircle2 size={12} />
                      </button>
                    )}
                    {!u.is_deleted ? (
                      <button onClick={() => softDelete(u)}
                              data-testid={`admin-user-delete-${i}`}
                              title="Soft delete"
                              className="p-1.5 border border-grid hover:bg-white/[0.04]"
                              style={{ color: "var(--bear)" }}>
                        <Trash2 size={12} />
                      </button>
                    ) : (
                      <button onClick={() => restore(u)}
                              data-testid={`admin-user-restore-${i}`}
                              title="Restore"
                              className="p-1.5 border border-grid hover:bg-white/[0.04]"
                              style={{ color: "var(--bull)" }}>
                        <RotateCcw size={12} />
                      </button>
                    )}
                  </td>
                </tr>
                {openKite === u.user_id && kiteAccs[u.user_id] && (
                  <tr className="border-b border-grid" style={{ background: "rgba(255,255,255,0.015)" }}>
                    <td colSpan={8} className="px-4 py-3">
                      <LinkedKiteControls acc={kiteAccs[u.user_id]} user={u} onSaved={load} />
                    </td>
                  </tr>
                )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};


const UpstoxProxyTester = ({ template, ip }) => {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const runTest = async () => {
    setLoading(true);
    setResult(null);
    try {
      // Tests the CURRENT (possibly unsaved) template + IP combo, same
      // substitution logic the backend applies at real order-placement time.
      const { data } = await api.post("/admin/upstox-proxy/test", { url: template, ip });
      setResult(data);
      if (data.ok) toast.success(`Upstox proxy OK (${data.latency_ms} ms)`);
      else toast.error(`Upstox proxy FAILED: ${data.error || `HTTP ${data.status_code}`}`);
    } catch (e) {
      // 2026-08 — the backend now catches every failure inside the endpoint
      // and returns a normal {ok:false, error, detail} 200 response, so this
      // catch block should rarely fire anymore. It's kept as a fallback for
      // genuine transport failures (network down, CORS, auth 401/403) — and
      // now surfaces the SERVER's error body when one exists, instead of
      // just axios's generic "Request failed with status code 500".
      const serverBody = e?.response?.data;
      const detail = serverBody?.detail || serverBody?.error
        || (serverBody ? JSON.stringify(serverBody) : null)
        || e.message;
      setResult({ ok: false, error: detail, status_code: e?.response?.status });
      toast.error(`Test failed: ${detail}`);
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="mt-2 space-y-2">
      <button
        onClick={runTest}
        disabled={loading}
        data-testid="upstox-proxy-test-btn"
        className="inline-flex items-center gap-2 px-3 py-1.5 border border-grid text-[10px] uppercase tracking-[0.2em] font-mono-data hover:border-white/60 transition-colors disabled:opacity-40"
      >
        <Radio size={12} className={loading ? "animate-pulse" : ""} />
        {loading ? "Testing…" : "Test Connection"}
      </button>
      {result && (
        <div
          data-testid="upstox-proxy-test-result"
          className="text-[11px] font-mono-data px-3 py-2 border"
          style={{
            borderColor: result.ok ? "var(--bull)" : "var(--bear)",
            color:       result.ok ? "var(--bull)" : "var(--bear)",
          }}
        >
          {result.ok ? (
            <>
              ✓ OK — HTTP {result.status_code} · {result.latency_ms} ms
              {result.proxy_used ? ` · via ${result.proxy_url_masked}` : " · direct"}
            </>
          ) : (
            <>
              ✗ FAIL — {result.error || `HTTP ${result.status_code}`}
              {result.latency_ms != null && ` · ${result.latency_ms} ms`}
              {result.proxy_used ? ` · via ${result.proxy_url_masked}` : ""}
              {/* Full traceback/detail from the backend's catch-all — collapsed
                  by default since it can run a couple thousand characters. */}
              {result.detail && (
                <details className="mt-2">
                  <summary className="cursor-pointer text-muted-2 normal-case">
                    Show full error detail
                  </summary>
                  <pre
                    data-testid="upstox-proxy-test-detail"
                    className="mt-1 whitespace-pre-wrap break-all text-[10px] text-muted-2 normal-case max-h-64 overflow-y-auto"
                  >
                    {result.detail}
                  </pre>
                </details>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};


// 2026-08 — Plain-English one-liners for every config key, so admins don't
// have to remember what e.g. `setup_c_v2_score_vwap_slope_down` does. Keyed
// by raw field name; the same key name means the same thing in every
// section it appears in, so one shared map covers all sections.
const CONFIG_HELP = {
  // --- Section 4 — Sniper Setups Autopilot ---
  // (min_score, min_risk_reward, stop_loss_pct, max_auto_picks_per_day,
  // alerts_enabled are shared entries defined below/above — same meaning here.)
  target_pct: "Auto-book profit once the option premium gains this %.",
  price_change_band_pct: "Only enter names whose day-over-day change is within ± this % (near-flat only).",
  min_rvol: "Minimum relative volume (today's volume ÷ 20-bar average) required to qualify.",
  min_rsi_bearish_floor: "BEARISH picks only: reject if intraday RSI is below this (already oversold — risk of shorting into a bounce). BULLISH picks are never affected.",
  max_rsi_bearish_ceiling: "BEARISH picks only: reject if intraday RSI is above this (still showing net bullish momentum — betting against the trend). BULLISH picks are never affected. Tightened 45→40 on 2026-09-19 backtest data (RSI 40-45 lost 0/6 trades).",
  rvol_window_gate_enabled: "1 = reject entries during the low-conviction window below unless RVOL clears the floor; 0 = disabled. Same key exists in Setup A/C config (section8) — set both to keep them in sync.",
  rvol_window_start_hh: "Low-conviction window start hour (IST, 24h). Entries inside this window are blocked unless RVOL clears rvol_window_min_rvol.",
  rvol_window_start_mm: "Low-conviction window start minute (IST).",
  rvol_window_end_hh: "Low-conviction window end hour (IST, 24h) — exclusive.",
  rvol_window_end_mm: "Low-conviction window end minute (IST) — exclusive.",
  min_option_oi_lots: "Commodities: minimum open interest (lots, from the Kite quote) for the option strike a signal uses, per underlying. Zerodha blocks new NRML orders in strikes with OI < 10 lots. If the configured strike fails the liquidity check, it tries one strike further OTM, then ATM, then 1 ITM; if none qualifies, no trade. The same check runs again just before the Kite BUY. 0 = off. Default 10.",
  min_option_oi_lots_default: "Commodities: the minimum OI (lots) for underlyings not listed in min_option_oi_lots. Default 10.",
  min_option_dte: "Commodities: options are taken from the NEAREST option expiry (not the futures month — Silver Mini futures are bi-monthly, so Oct options settle into the Nov future). A series with fewer than this many calendar days to expiry rolls to the next series. 1 = never buy on expiry day. Per underlying. Added 2026-10-13.",
  min_option_dte_default: "Commodities: min_option_dte for underlyings not listed. Default 1.",
  max_option_spread_pct: "Commodities: a strike is only used if it has a two-sided book (bid and ask), has traded today, and its bid/ask spread is at most this % of the mid price. Stops trades on dead strikes with stale LTPs. 0 = no spread check. Default 5.",
  live_buy_buffer_pct: "Sniper live BUY: starting LIMIT = LTP − this %. Default 0 (start at LTP). Was a fixed 5%, which left rising options unfilled (DABUR, SHRIRAMFIN, ONGC chase timeouts).",
  live_buy_max_chase_pct: "Sniper live BUY: the chase may raise the LIMIT up to LTP + this % (at least 2 ticks). Default 2.",
  live_chase_interval_sec: "Sniper live BUY: seconds between chase steps (0.5% of LTP, min 1 tick). Default 15.",
  live_chase_timeout_sec: "Sniper live BUY: cancel an unfilled BUY after this many seconds (the pick continues as paper). Default 120.",
  stock_lots_per_trade: "Lots per stock-option trade (live + paper) for Sniper, Candle Breakout, VWAP Bounce, OI Buildup and Bearish PE. 1 = always 1 lot (default). 2+ = fixed lots. 0 = old monthly-cycle auto (1 lot first 10 days after expiry, then 2). Linked Zerodha copies follow their own lot multiplier on top. Momentum OI uses its own lots_per_trade. Added 2026-10-16.",
  stock_lots_sniper: "Sniper lots override. 0 = follow stock_lots_per_trade; 1+ = fixed lots for Sniper only.",
  stock_lots_candle_breakout: "Candle Breakout lots override. 0 = follow stock_lots_per_trade; 1+ = fixed lots for CB only.",
  stock_lots_vwap_bounce: "VWAP Bounce lots override. 0 = follow stock_lots_per_trade; 1+ = fixed lots for VWAP Bounce only.",
  stock_lots_oi_buildup: "OI Long Buildup (Section 6) lots override. 0 = follow stock_lots_per_trade.",
  stock_lots_bearish_pe: "Bearish PE lots override. 0 = follow stock_lots_per_trade.",
  setup_a_min_breadth_pct: "VWAP Bounce Setup A: no new LONG entry (paper and live) when fewer than this % of F&O stocks closed above their 20-day EMA the previous day (Journal → Market regime card shows today's value). 70 Setup A trades Aug–Oct: ≥45% PF 1.52 (43 trades), 35–45% 0.98, 25–35% 0.37, <25% 0.27. Skipped picks are replayed after the close (Journal → Skipped picks card, VWAP Bounce). Default 45. 0 = off. Added 2026-10-19.",
  enforce_mode: "Risk Governance: 'shadow' (default) = the checks below only log what they would block (Live Orders keep going; only the Hard Kill blocks). 'enforce' = any failing check stops the LIVE buy for that pick — it is still recorded as a paper trade with reason 'risk_governance:<check>'. Exits are never blocked. Every hit is saved in risk_governance_events. Added 2026-10-19.",
  daily_loss_limit_rs: "Risk Governance: combined daily LIVE loss limit in ₹ across stocks AND commodities. Once today's live P&L reaches −this amount, no new live entries for the rest of the day (open positions keep their normal exits). Day P&L = your Kite day positions P&L (incl. open M2M); when Kite can't be read, the sum of today's closed live trades that actually filled (net of charges). Needs enforce_mode = 'enforce' to block. 0 = off. Added 2026-10-19.",
  stocks_symbol_daily_buy_cap: "Risk Governance: max live BUY orders per stock per day, shared across all setups. Counts Kite (live_order_id) and Upstox orders. Default 4.",
  commodities_symbol_daily_buy_cap: "Risk Governance: max live BUY orders per commodity underlying per day. Default 3.",
  stocks_max_open_positions: "Risk Governance: max open LIVE stock positions at the same time, across all setups. Default 2.",
  commodities_max_open_positions: "Risk Governance: max open LIVE commodity positions at the same time. Default 2.",
  nifty_down_threshold_pct: "Risk Governance: no new live entries while Nifty is down this % or more vs yesterday's close. Default −2.",
  nifty_up_threshold_pct: "Risk Governance: no new live entries while Nifty is up this % or more vs yesterday's close. Default 2.",
  setup_a_breadth_gate_mode: "VWAP Bounce Setup A breadth gate mode: 'block' (default) skips the entry; 'log' records it on the trade (setup_a_breadth.would_block) without skipping; 'off' = not evaluated.",
  live_ce_block_late_entry:"Sniper: no LIVE CE buy when Nifty is already up 0.2% or more vs yesterday's close at entry (the Journal's late-entry flag) — the pick is still recorded as a paper trade (reason 'ce_late_entry'). Late PE trades are handled by live_pe_late_needs_bearish. 15-Sep to 15-Oct: CE late 2W/6L, CE otherwise 3W/1L. 1 = on (default), 0 = off. Added 2026-10-16.",
  live_pe_late_needs_bearish: "Sniper: a PE pick taken after Nifty has already fallen 0.2% or more (late entry) goes LIVE only when today's market regime is BEARISH (under 35% of F&O stocks above their 20-day EMA — Journal → Market regime card). Otherwise it is recorded as a paper trade (reason 'pe_late_entry_not_bearish'). 15-Sep to 19-Oct: late PE on BEARISH days 3W/0L, on MIXED days 0W/3L; non-late entries 12W/1L. Unknown regime never blocks. 1 = on (default), 0 = off. Added 2026-10-19.",
  live_guard_max_consec_losses: "Sniper live guard: switch Sniper LIVE off automatically after this many losing live trades in a row (paper continues; you get a Telegram alert). Counts only live trades taken since live was last switched on — re-enabling starts a fresh record. 0 = off. Default 4. Added 2026-10-19.",
  live_guard_min_trades_for_pf: "Sniper live guard: once there are at least this many closed live trades since live was switched on, check the profit factor (see live_guard_min_pf). 0 = off. Default 10.",
  live_guard_min_pf: "Sniper live guard: switch Sniper LIVE off when the live profit factor (₹ won ÷ ₹ lost, after charges) is below this, once live_guard_min_trades_for_pf trades are closed. Default 1.0.",
  live_guard_pause_on_order_error: "Sniper live guard: 1 = switch Sniper LIVE off on any order problem — a BUY the broker rejected or did not accept, or an exit SELL that errored (normal cases like 'positions unavailable, retrying', 'BUY never filled' or 'already exited in Kite' don't count). Chase timeouts don't count. 0 = off. Default 1.",
  live_ce_max_vwap_ext_pct: "Sniper: no LIVE CE buy when the stock is already more than this % above its VWAP — the pick is still recorded as a paper trade (reason 'ce_extended_above_vwap'). PE trades are not affected. Aug–Oct: CE ≤1% above VWAP PF 1.66, >1% PF ~0.65. 0 = off. Default 1.0. Added 2026-10-15.",
  forming_bar_min_age_min: "Sniper: evaluate the still-forming 15-min bar once it is at least this many minutes old (default 10; before the Kite move Sniper used 5–10 min old bars: 2.3 trades/day, PF 1.37 — the 10-min-old ones did best). Younger bars are skipped and the last closed bar is used. 15 = closed bars only (the 30-Sep behaviour, ~0.5 trades/day). Added 2026-10-09.",
  chase_shadow_opt_3m_pct: "Sniper anti-chase: flag an entry when the option premium rose at least this % in the 3 minutes before entry. Blocks the LIVE buy when live_block_premium_chase = 1 (paper still recorded) — see the Anti-chase card on the Sniper page. Default 8. Added 2026-10-13.",
  chase_shadow_opt_5m_pct: "Sniper anti-chase: flag an entry when the option premium rose at least this % in the 5 minutes before entry. Blocks the LIVE buy when live_block_premium_chase = 1. Default 10.",
  live_block_premium_chase: "Sniper: 1 = no LIVE buy when the option premium already ran up (chase_shadow_opt_3m_pct / chase_shadow_opt_5m_pct) — recorded as a paper trade, reason 'premium_chase'. 14 days of logging: flagged entries 0W/4L. Stock-ATR / RVOL flags stay log only. 0 = log only. Added 2026-10-28.",
  live_red_candle_pe_block: "Sniper red-candle filter for PE picks: 0 = log only (default), 1 = block the live PE buy as before. Since Oct its PE skips all won (HINDUNILVR, VBL, DABUR +75%), its CE skips all lost. CE is always filtered. Added 2026-10-28.",
  live_red_candle_min_red: "Sniper red-candle filter: block only when red candles outnumber green AND there are at least this many red ones among the last 5 × 5-min candles on the option (default 3). Stops thin-data blocks like 1 red of 3. Added 2026-10-28.",
  chase_shadow_stock_atr: "Sniper, LOG ONLY (anti-chase test): flag an entry when the stock moved at least this many ATRs in the trade's direction in the last 5 minutes. Default 1.0.",
  chase_shadow_rvol: "Sniper, LOG ONLY (anti-chase test): flag an entry when the pick's RVOL (volume burst) is at least this. Default 4.",
  shadow_band_pct: "Sniper, LOG ONLY: picks that fail just the live day-change band (price_change_band_pct) but pass with this wider band are tracked on the real option price — see the Band test card on the Sniper page. 0 = off. Live trading is unchanged. Added 2026-10-09.",
  paper_entry_at_market: "Commodities PAPER trades only: 1 = paper entry books at the live option quote (no limit-offset discount), matching what real Kite orders actually got; 0 = old behaviour (quote − limit_order_offset). Real orders are unaffected either way. Added 2026-10-09.",
  tp_sl_mode: "Commodities stop/target: \"pct\" = % of the entry premium per underlying (sl_pct_by_underlying / tp_pct_by_underlying), re-applied to the real Kite fill; \"rs\" = the fixed ₹ amounts (stop_loss_rs / target_profit_rs) per lot, converted with the real lot units (100 bbl for CRUDEOIL). Default pct. Added 2026-10-28.",
  sl_pct_by_underlying: "Commodities stop-loss, % below the entry premium, per underlying (used when tp_sl_mode = pct). CRUDEOIL 4.5 ≈ ₹1,750 on a ₹390 premium. Others use sl_pct_default.",
  tp_pct_by_underlying: "Commodities target, % above the entry premium, per underlying (used when tp_sl_mode = pct). Others use tp_pct_default.",
  sl_pct_default: "Commodities stop-loss % for underlyings not listed in sl_pct_by_underlying. Default 6.",
  tp_pct_default: "Commodities target % for underlyings not listed in tp_pct_by_underlying. Default 6.",
  setup_a_target_pct: "Setup A only: fixed target, % gain on the option premium (default 8). 0 = use the old dynamic 6–12% band. Added 2026-10-09 after replaying 66 Setup A trades (8% was best: 53% win, PF 1.14).",
  setup_a_stop_loss_pct: "Setup A only: fixed stop-loss, % drop on the option premium (default 8, entered as a positive number). 0 = use the old dynamic 8–12% band. Keep it ≤ setup_a_target_pct or the risk/reward check rejects the pick.",
  setup_a_day_change_gate_enabled: "Setup A only: 1 = skip a LONG when the stock is already up vs its 09:15 open (above setup_a_max_day_change_pct) — a 'bounce' on a flat/up stock is a chase; 0 = off. Added 2026-10-07 from 66 closed Setup A trades.",
  setup_a_max_day_change_pct: "Setup A only: highest allowed % change vs the 09:15 open at entry (default -0.25 = the stock must be down at least 0.25% on the day). Aug 11–Sep 21: ≤ -0.25% won 62% (+113% total), above it 30% (-77%).",
  rvol_window_min_rvol: "Minimum RVOL required to enter during the low-conviction window (default 12:45–13:45 IST — 14-day journal data showed this hour losing heavily across both directions and both Sniper + VWAP Bounce).",
  rsi_overbought_ceiling: "Not yet enforced — observation only. RSI above this + weak RVOL/ADX flags a trade as 'overbought but unsupported' (would_block_* fields on the trade row), for evaluating later whether either rule should become a real gate.",
  min_rvol_overbought: "Not yet enforced — observation only. Paired with rsi_overbought_ceiling: RVOL below this while RSI is overbought sets would_block_rsi_rvol_rule=true on the trade row.",
  adx_trend_floor: "Hard reject any entry with ADX(14) below this — direction-agnostic, applies to every pick regardless of RSI (raised 20→30 on 2026-09-19 backtest data; ADX 20-30 was losing, ADX 30+ was closer to breakeven or profitable).",

  // --- Section 5 — Candle Breakout ---
  scan_start_hh: "Scan window opens at this time (IST).",
  scan_start_mm: "Scan window opens at this time (IST).",
  scan_end_hh: "Scan window closes — no new picks considered after this time.",
  scan_end_mm: "Scan window closes — no new picks considered after this time.",
  max_auto_picks_per_day: "Maximum auto-entries allowed per day.",
  min_anticipated_pct: "Minimum expected move % for a candle to qualify.",
  book_profit_pct: "Auto-book profit once the option premium gains this %.",
  stop_loss_pct: "Auto-exit once the option premium loses this % (negative value).",
  target_min_pct: "Lower bound for the dynamic profit target.",
  entry_window_restriction_enabled: "1 = only auto-enter inside the two windows below; 0 = disabled (falls back to the plain 14:00 no-new-entries cutoff only). Added 2026-09-19: backtest found CB's win rate is bimodal by entry time (best 09:30-10:30 & 11:30-12:30, worse 10:30-11:30 & 12:30-14:00), not a simple morning-to-afternoon decay.",
  entry_window_1_start_hh: "Window 1 start hour (IST, 24h).",
  entry_window_1_start_mm: "Window 1 start minute (IST).",
  entry_window_1_end_hh: "Window 1 end hour (IST, 24h).",
  entry_window_1_end_mm: "Window 1 end minute (IST).",
  entry_window_2_start_hh: "Window 2 start hour (IST, 24h).",
  entry_window_2_start_mm: "Window 2 start minute (IST).",
  entry_window_2_end_hh: "Window 2 end hour (IST, 24h).",
  entry_window_2_end_mm: "Window 2 end minute (IST).",
  target_max_pct: "Upper bound for the dynamic profit target.",
  target_base_pct: "Default profit target used when nothing else overrides it.",
  supertrend_period: "Number of candles used to calculate the Supertrend indicator.",
  supertrend_multiplier: "ATR multiplier controlling how far the Supertrend band sits from price.",
  rsi_min: "Underlying RSI(14) must be at least this to qualify.",
  rsi_5m_max: "Candle Breakout: block auto-entry (paper and live) when the underlying's 5-min RSI at entry is at or above this. 0 = off.",
  vwap_distance_max_pct: "Reject picks whose price is more than this % away from VWAP.",
  max_option_premium_move_pct: "Reject a pick if the premium already moved more than this % (avoids chasing).",
  min_risk_reward: "Minimum target ÷ stop-loss ratio required to enter.",

  // --- Section 6 — OI Long Buildup ---
  min_score: "Composite quality score a candidate must reach to qualify.",
  min_bullish_tf: "Number of timeframes that must show a bullish trend.",
  max_vwap_distance_pct: "Reject picks whose price is more than this % away from VWAP.",
  min_oi_change_pct: "Minimum rise in open interest to count as a genuine long buildup.",
  min_price_change_pct: "Lower bound for the underlying's intraday % change.",
  max_price_change_pct: "Upper bound for the underlying's intraday % change.",
  correction_vol_surge_pct: "Volume spike % used to flag a correction inside the buildup.",
  rvol_threshold: "Latest volume must be at least this × the recent average.",
  rvol_lookback: "Number of candles averaged for the relative-volume comparison.",
  break_lookback: "Candles looked back when checking for a breakout.",
  nifty_min_change_pct: "Nifty counts as 'stable' when above VWAP or its change % is above this floor.",

  // --- Section 7 — Live Orders ---
  buy_buffer_pct: "BUY limit order placed this % below the live price.",
  sell_buffer_pct: "SELL limit order placed this % below the live price to help it fill.",
  chase_wait_minutes: "Minutes to wait before nudging an unfilled order closer to market.",
  rr_floor: "Minimum risk:reward required before placing a live order.",
  max_move_pct: "Reject a live order if premium already moved beyond this % (stale-signal guard).",
  history_days: "Days of order history retained and shown.",
  cross_section_alert_threshold: "Simultaneous signals across setups needed to trigger a combined alert.",

  // --- Section 8 — VWAP Bounce (Setup A/B/C) ---
  atr_sl_multiplier: "Stop-loss distance = this × ATR(14), instead of a flat %.",
  scan_interval_min: "Minutes between scans.",
  vwap_touch_pct: "How close price must come to VWAP to count as a 'touch'.",
  min_body_ratio: "Candle body must be at least this fraction of its full range.",
  min_underlying_price: "Skip stocks trading below this price (₹).",
  eod_force_exit_hh: "All open positions force-closed at this time, regardless of P&L.",
  eod_force_exit_mm: "All open positions force-closed at this time, regardless of P&L.",
  entry_rvol_threshold: "Minimum relative volume required for Setup A/B long entries.",
  entry_rvol_lookback: "Candles averaged for the entry relative-volume check.",
  entry_vwap_distance_min_pct: "Price must be at least this far from VWAP to enter.",
  entry_vwap_distance_max_pct: "Price must be no farther than this from VWAP to enter.",
  entry_rsi_min: "Minimum RSI required at entry.",
  entry_rsi_max: "Maximum RSI allowed at entry.",
  entry_option_premium_max_gain_pct: "Skip entry if the premium already gained more than this % (chasing guard).",
  entry_vwap_touch_tolerance_pct: "Setup B: prior low may sit this % above VWAP and still count as a touch.",
  setup_a_buy_buffer_pct: "Setup A: 0 = market order; > 0 = limit order this % below live price.",
  setup_b_buy_buffer_pct: "Setup B: limit order placed this % below live price.",
  setup_c_buy_buffer_pct: "Setup C: limit order (buying the PUT) placed this % below live price.",
  scan_atr_distance_multiplier: "Candle's distance from VWAP must be within this × ATR to qualify.",
  scan_volume_avg_lookback: "Candles averaged for the scanner's volume confirmation.",
  scan_volume_avg_multiplier: "Latest 5-min volume must exceed this × the recent average.",
  scan_rvol_threshold: "Mandatory relative-volume floor for Setup A/B (Setup C is unaffected).",
  scan_rvol_lookback: "Candles averaged for the scanner's relative-volume gate.",
  excluded_symbols: "Tickers skipped by both live scanning and backtests.",
  setup_a_start_hh: "Setup A entry window (IST).",
  setup_a_start_mm: "Setup A entry window (IST).",
  setup_a_end_hh: "Setup A entry window (IST).",
  setup_a_end_mm: "Setup A entry window (IST).",
  setup_b_start_hh: "Setup B entry window (IST).",
  setup_b_start_mm: "Setup B entry window (IST).",
  setup_b_end_hh: "Setup B entry window (IST).",
  setup_b_end_mm: "Setup B entry window (IST).",
  setup_c_start_hh: "Setup C entry window (IST).",
  setup_c_start_mm: "Setup C entry window (IST).",
  setup_c_end_hh: "Setup C entry window (IST).",
  setup_c_end_mm: "Setup C entry window (IST).",
  setup_c_v2_min_underlying_price: "Setup C v2: skip stocks trading below this price (₹).",
  setup_c_v2_daily_rsi_max: "Setup C v2: daily RSI(14) must stay below this.",
  setup_c_v2_vwap_touch_pct: "Setup C v2: VWAP touch tolerance %.",
  setup_c_v2_vwap_touch_lookback: "Setup C v2: candles checked for a valid VWAP touch.",
  setup_c_v2_rvol_threshold: "Setup C v2: minimum relative volume required.",
  setup_c_v2_rvol_lookback: "Setup C v2: candles averaged for the relative-volume check.",
  setup_c_v2_min_score: "Setup C v2: composite score needed to qualify as a signal.",
  setup_c_v2_near_miss_floor: "Setup C v2: scores at/above this (but below qualifying) show as 'near-miss'.",
  setup_c_v2_score_two_reds: "Bonus points when two consecutive red candles appear.",
  setup_c_v2_score_three_reds: "Bonus points when three consecutive red candles appear.",
  setup_c_v2_score_strong_body: "Bonus points when the signal candle has a strong (large) body.",
  setup_c_v2_score_rising_vol: "Bonus points when volume is rising into the signal.",
  setup_c_v2_score_falling_rsi: "Bonus points when RSI is falling.",
  setup_c_v2_score_vwap_slope_down: "Bonus points when VWAP itself is sloping down.",
  setup_c_v2_score_htf_slope_down: "Bonus points when the higher-timeframe trend is sloping down.",
  setup_c_v2_score_small_lower_wick: "Bonus points when the candle has a small lower wick.",
  setup_c_v2_score_atr_expansion: "Bonus points when ATR (volatility) is expanding.",
  setup_c_v2_body_min_pct: "Candle body must be at least this % of its range to earn the strong-body bonus.",
  setup_c_v2_rising_vol_multiplier: "Volume must exceed this × the last-2-candle average for the rising-volume bonus.",
  setup_c_v2_rsi_falling_ceiling: "RSI must be below this (and falling) to earn the bonus.",
  setup_c_v2_lower_wick_max_ratio: "Wick-to-body ratio must be at or below this for the small-wick bonus.",
  setup_c_v2_vwap_slope_max_pct: "VWAP slope must be below this (more negative) to earn the bonus.",
  setup_c_v2_vwap_slope_lookback: "Candles used to measure VWAP slope.",
  setup_c_v2_htf_slope_max_pct: "Higher-timeframe slope must be below this to earn the bonus.",
  setup_c_v2_htf_slope_lookback: "Candles used to measure the higher-timeframe slope.",
  setup_c_v2_atr_expansion_min: "ATR(5) must be at least this × ATR(20) to earn the bonus.",
  setup_a_excluded_symbols: "Tickers skipped only by Setup A (layered on top of the global exclusion list).",
  setup_a_v3_market_trend_ema_period: "EMA period used to gauge overall market trend.",
  setup_a_v3_adx_period: "Candles used to calculate ADX (trend strength).",
  setup_a_v3_adx_min: "Minimum ADX required — filters out choppy, trendless markets.",
  setup_a_v3_vwap_max_distance_pct: "Maximum allowed distance from VWAP.",
  setup_a_v3_vwap_extended_pct: "Distance from VWAP beyond which price is considered 'extended'.",
  setup_a_v3_pullback_max_red_candles: "Maximum consecutive red candles allowed in the pullback.",
  setup_a_v3_pullback_max_pct_of_up_move: "Pullback can retrace at most this % of the prior up-move.",
  setup_a_v3_pullback_max_bar_atr_mult: "Each pullback candle's range is capped at this × ATR.",
  setup_a_v3_bounce_body_min_pct_of_range: "Bounce candle's body must be at least this % of its range.",
  setup_a_v3_bounce_close_upper_range_pct: "Bounce candle must close in the top this % of its range.",
  setup_a_v3_volume_bounce_min_x_20avg: "Bounce candle's volume must exceed this × the 20-candle average.",
  setup_a_v3_rsi_period: "Candles used to calculate RSI.",
  setup_a_v3_rsi_min: "Minimum RSI required to enter.",
  setup_a_v3_rsi_max: "Maximum RSI allowed to enter.",
  setup_a_v3_htf_ema_period: "EMA period used for the higher-timeframe trend check.",
  setup_a_v3_min_rr_ratio: "Minimum risk:reward required to enter.",
  setup_a_v3_sl_atr_multiplier_below_vwap: "Stop-loss distance below VWAP, as a multiple of ATR.",
  setup_a_v3_trap_max_gap_up_pct: "Gap-ups beyond this % are treated as a possible trap and skipped.",
  setup_a_v3_trap_max_upper_wick_mult: "Upper wick beyond this × body is treated as a trap signal.",
  setup_a_v3_trap_max_pre_bounce_red: "Too many red candles before the bounce is treated as a trap.",
  setup_a_v3_trap_entry_cutoff_hh: "No new entries considered after this time (trap-avoidance cutoff).",
  setup_a_v3_trap_entry_cutoff_mm: "No new entries considered after this time (trap-avoidance cutoff).",
  setup_a_v3_score_min: "Composite score (0-100) required to fire a signal.",
  // 2026-09-21 — removed tooltips for setup_a_v3_partial_booking_enabled/
  // qty_pct, setup_a_v3_partial_target_rr, setup_a_v3_trail_ema_period
  // alongside their DEFAULT_CONFIGS removal (dead: no partial-booking or
  // EMA-trailing-stop logic anywhere reads them).
  setup_b_rsi_rising_enabled: "1 = require RSI to be strictly rising across 3 candles.",
  setup_b_rsi_period: "Candles used to calculate RSI for this filter.",
  setup_b_rsi_floor_intraday: "Minimum intraday RSI required.",
  setup_b_vwap_slope_enabled: "1 = require VWAP itself to be sloping upward.",
  setup_b_vwap_slope_lookback_bars: "Candles used to measure VWAP slope.",
  setup_b_vwap_slope_min_pct: "Minimum upward VWAP slope required.",
  setup_b_session_high_check_enabled: "1 = skip longs too close to the session high without a prior pullback.",
  setup_b_session_high_proximity_pct: "Distance from the session high considered 'too close'.",
  setup_b_session_prior_pullback_pct: "Minimum prior pullback % required to bypass the session-high check.",
  setup_b_upper_wick_enabled: "1 = require the signal candle's upper wick to stay small.",
  setup_b_upper_wick_max_ratio: "Maximum upper-wick-to-body ratio allowed.",
  setup_b_strict_volume_rising: "1 = require volume to rise across all 3 confirmation candles.",
  setup_b_htf_ma_slope_enabled: "1 = require the 15-min moving average to be sloping up.",
  setup_b_htf_ma_period: "Period of the higher-timeframe moving average.",
  setup_b_htf_ma_lookback_bars: "Candles used to measure the higher-timeframe MA slope.",
  setup_b_htf_ma_min_slope_pct: "Minimum upward slope required for the higher-timeframe MA.",
  // 2026-09-21 — removed tooltips for setup_a_rsi_rising_enabled/
  // setup_a_rsi_floor_intraday/setup_a_upper_wick_enabled/
  // setup_a_upper_wick_max_ratio and setup_mood_gating_enabled alongside
  // their DEFAULT_CONFIGS removal (dead — see the matching admin_service.py
  // comments; chat: "Review and remove dead configs for VWAP Bounce").
  min_sell_profit_pct: "Minimum actual profit (vs the BUY fill) required before booking a 'profit' exit.",
  setup_c_time_stop_enabled: "1 = force-close a Setup C trade early if it's stalled (see below); 0 = disabled, resolves only via target/SL/EOD.",
  setup_c_time_stop_min: "Setup C: minutes a trade can sit inside the dead-zone band before it's force-closed as TIME_STOP.",
  setup_c_time_stop_pnl_band_pct: "Setup C: dead-zone band (±%). Trade is only time-stopped if |P&L| is still inside this band — trades already trending toward target/SL are left alone.",
  setup_c_rr_geometry_exit_enabled: "1 = Setup C exits when the underlying crosses its own sl/target levels (2:1 RR) instead of the flat target %/SL % below — backtest showed this turns Setup C profitable (avg P&L -0.62% -> +3.22%). 0 = Setup C uses the same flat % exit as Setup A/B.",

  // --- Section 9 — Setup E V2 (shown alongside the read-only markets summary) ---
  quick_leg_target_pct: "Quick Leg books in full once premium is up this % (no trailing).",

  // --- Section 10 — VWAP + OI Smart Money ---
  live_trading_enabled: "1 = allow this setup to place real orders; 0 = paper trading only.",
  alerts_enabled: "1 = send Telegram/UI alerts when a signal fires.",
  rvol_min: "Minimum relative volume required (all gates must pass).",
  price_vwap_max_pct: "Price must stay within this % of VWAP.",
  ema_short: "Period of the short EMA used in the trend gate.",
  ema_long: "Period of the long EMA used in the trend gate.",
  pullback_lookback: "Candles checked for a valid pullback.",
  top_n: "Maximum ranked candidates considered per scan.",
  entry_window_1_start_hh: "First entry window (IST).",
  entry_window_1_start_mm: "First entry window (IST).",
  entry_window_1_end_hh: "First entry window (IST).",
  entry_window_1_end_mm: "First entry window (IST).",
  entry_window_2_start_hh: "Second entry window (IST).",
  entry_window_2_start_mm: "Second entry window (IST).",
  entry_window_2_end_hh: "Second entry window (IST).",
  entry_window_2_end_mm: "Second entry window (IST).",
  eod_hh: "All open positions force-closed at this time (IST).",
  eod_mm: "All open positions force-closed at this time (IST).",
  risk_per_trade_pct: "Capital risked per trade, as a % of account size.",
  min_rr_ratio: "Minimum risk:reward required to enter.",
  sl_atr_multiplier_below_vwap: "Stop-loss distance below VWAP, as a multiple of ATR.",
  partial_1_r_multiple: "R-multiple at which the first partial exit fires.",
  partial_1_qty_pct: "% of quantity booked at the first partial exit.",
  partial_2_r_multiple: "R-multiple at which the second partial exit fires.",
  partial_2_qty_pct: "% of quantity booked at the second partial exit.",
  trail_ema_period: "EMA period used to trail the stop on the remaining quantity.",
  pullback_max_consecutive_red: "Maximum consecutive red candles allowed in the pullback.",
  max_entries_per_day: "Maximum auto-entries allowed per day.",
  entry_buffer_pct: "Fill price discount below the confirming candle's close, so entries aren't taken at the top of the bounce.",

  // --- Linear Accumulation / Steady Intraday Trend ---
  scanner_enabled: "1 = scanner actively looks for signals.",
  paper_trading_enabled: "1 = log paper trades for every signal found.",
  option_reco_enabled: "1 = recommend a specific option strike alongside each signal.",
  min_volume_multiplier: "Minimum volume vs. average required.",
  min_adx: "Minimum ADX (trend strength) required.",
  rsi_max: "Maximum RSI allowed.",
  max_gap_up_pct: "Skip stocks that gapped up more than this % at open.",
  distance_from_high_pct: "Price must be within this % of the day's high.",
  target_r_multiple: "Profit target, expressed as a multiple of risk (R).",
  atr_period: "Candles used to calculate ATR.",
  adx_atm_threshold: "ADX above this recommends an at-the-money strike.",
  adx_itm_threshold: "ADX above this recommends an in-the-money strike.",
  concurrency: "Maximum option-chain lookups run in parallel during a scan.",
  market_open_hh: "Market open time (IST), used for scan-window math.",
  market_open_mm: "Market open time (IST), used for scan-window math.",
  market_close_hh: "Market close time (IST), used for scan-window math.",
  market_close_mm: "Market close time (IST), used for scan-window math.",
  near_miss_floor: "Scores at/above this (but below the qualifying score) show as 'near-miss'.",
  smoothness_min: "Minimum 'Trend Smoothness' score required.",
  structure_lookback: "Candles checked for Higher-Highs / Higher-Lows structure.",
  structure_min_hh: "Minimum Higher Highs required in the lookback.",
  structure_min_hl: "Minimum Higher Lows required in the lookback.",
  vwap_max_below_closes: "Maximum closes allowed below VWAP in the lookback.",
  pullback_max_pct: "Maximum pullback, as a % of the prior impulse move.",
  day_high_bonus_pct: "Distance from the day's high inside which a bonus score is awarded.",
  upper_circuit_pct: "Skip stocks trading within this % of their upper circuit.",

  // --- Shared option-liquidity gates ---
  min_option_premium: "Reject option legs quoting below this price (₹).",
  max_option_spread_pct: "Reject legs whose bid-ask spread exceeds this % of the mid price.",
  settlement_guard_days: "Shift to a further OTM strike when monthly expiry is within this many days.",

  // --- Kite Execution (real CRUDEOIL orders via Zerodha, Phase 1) ---
  enabled_underlyings: "Underlyings this real-order path applies to (CRUDEOIL, SILVERM). Combine with live_strategies to limit which strategies trade for real.",
  live_strategies: "Per underlying, which strategies may place a REAL Kite order: reversal = Reversal Entry Zones, vwap_momentum = MCX VWAP Momentum. Default {\"CRUDEOIL\": [\"reversal\", \"vwap_momentum\"], \"SILVERM\": [\"reversal\"]}. An underlying not listed allows every strategy; [] allows none. Added 2026-10-09.",
  market_protection_pct: "Slippage bound (%) for MARKET orders — Kite rejects a market_protection of 0 outright.",
  quantity_lots: "Lots per confirmed signal, one entry per underlying (e.g. {\"CRUDEOIL\": 1}).",
  confirm_expiry_minutes: "A pending confirmation older than this is auto-expired — a stale tap can't fire an order against a market that's moved on.",

  // --- Bearish PE Setup V1 ("PE Opportunity Engine") ---
  trading_start_time: "Scheduled scans/auto-entries only run at/after this IST time (HH:MM, weekdays). Resolving already-open trades and the 15:15 EOD close are unaffected.",
  trading_end_time: "Scheduled scans/auto-entries stop at this IST time (HH:MM, weekdays). Resolving already-open trades and the 15:15 EOD close are unaffected.",
  final_score_min: "Blended score (underlying × option, 0-100) required to auto-enter a trade — the one hard gate.",
  final_score_high_priority: "Blended score at/above this is flagged HIGH PRIORITY instead of GOOD.",
  underlying_score_min: "Informational only — underlying-structure score (0-100) treated as a WATCH-tier floor, not a hard gate.",
  option_score_min: "Informational only — option-liquidity score (0-100) treated as a floor, not a hard gate.",
  underlying_weight: "Weight given to the underlying-structure score in the blended final score (0-1).",
  option_weight: "Weight given to the option-liquidity score in the blended final score (0-1).",
  late_entry_minutes: "A breakdown older than this many minutes is treated as stale and rejected.",
  support_lookback_bars: "5-min candles used for the rolling support level (ramps up from a floor early in the session — see min_rvol_hard_gate note).",
  bullish_regime_score_penalty: "Points subtracted from the regime score component when NIFTY reads BULLISH.",
  min_rvol_hard_gate: "Hard reject below this RVOL, independent of score (added 2026-09-10 after several low-volume entries).",
  rsi_hard_ceiling: "Hard reject when the underlying's RSI is at/above this, independent of score (e.g. 35 = only enter when RSI < 35).",
  rel_weakness_hard_ceiling: "Hard reject when relative_weakness (stock's day change% minus NIFTY's) is above this, independent of score (e.g. -2.0 = only enter when the stock is underperforming NIFTY by at least 2pts).",
  max_spread_pct: "Reject/badge the PE leg when its bid-ask spread exceeds this % of the mid price.",
  min_oi: "Informational badge threshold for 'meaningful OI' on the PE leg.",
  max_concurrent_trades: "Maximum open Bearish PE paper positions at once.",
  max_per_sector: "Maximum open positions in the same sector at once.",
  cooldown_minutes: "Minutes a symbol stays in cooldown after a signal, before it can re-enter.",
  emergency_sl_pct: "Option premium backstop stop-loss (%) — the PRIMARY stop is structural (underlying reclaims the breakdown support level); this only fires if that never triggers.",

  // --- System ---
  upstox_sync_interval_sec: "Seconds between Upstox order/position sync ticks.",
  upstox_sync_enabled: "1 = run the Upstox auto-sync loop; 0 = disabled.",
  upstox_conn_failure_streak: "Consecutive Upstox failures needed before an alert fires.",
  backtest_slippage_pct: "Slippage % applied to each side (entry + exit) of a backtest fill.",
};

// `lots_per_trade` means slightly different things in Setup E (section9,
// enables the Quick Leg split) vs VWAP Bounce (section8, flat multi-lot
// sizing) — override per-section instead of one shared string.
// Enum-valued string config fields -> dropdown options (per section).
const CONFIG_ENUMS = {
  momentum_oi: {
    live_entry_mode: ["LTP", "ENTRY_HIGH", "DISCOUNT"],
    premium_gates_mode: ["log", "block", "off"],
  },
};

const CONFIG_HELP_BY_SECTION = {
  section8: { lots_per_trade: "Lots placed per live trade. 1 = legacy single-lot behaviour." },
  section9: { lots_per_trade: "Lots bought per entry. ≥ 2 enables the Quick Leg split (1 lot books early at the target below; the rest rides the full T1/T2/trail engine)." },
  momentum_oi: {
    live_entry_mode: "Live BUY LIMIT price. LTP = fresh LTP; ENTRY_HIGH = min(fresh LTP, setup entry-high); DISCOUNT = LTP − live buy buffer %. Never above entry-high; no BUY if LTP is already above it.",
    live_buy_buffer_pct: "Used only when live entry mode = DISCOUNT.",
    max_paper_trades_per_day: "Setups beyond this per day are recorded in the run as skipped: paper_cap.",
    eod_alert_time: "Telegram alert for any live position still open at/after this time (HH:MM IST).",
    stale_claim_minutes: "A trade stuck in PENDING_ENTRY longer than this is reconciled (ENTRY_ERROR or adopted from the broker order).",
    use_fixed_pct_exits: "1 = exit at fixed Target % / Stop loss % of the real entry premium (single target, no T1 breakeven stage). 0 = scanner's own SL/T1/T2 plan.",
    target_pct: "Profit target, % of the entry premium (fixed-% exits).",
    stop_loss_pct: "Stop loss, % below the entry premium (fixed-% exits). The 15m spot-close SL still applies too.",
    breakeven_at_pct: "Momentum OI: once the option is up this % from the entry, the stop moves to the entry price (exit reason BREAKEVEN_STOP if it comes back). Must be below target_pct. 0 = off. Set 27-Oct for PAPER to stop 7 / break-even 8 / target 20 after the exit-rule study (replayed 13–27 Oct: PF 1.91 vs 1.22 for plain 8 / 7). The outcome tracker keeps replaying the old 8 / 7 for comparison.",
    min_entry_rvol_proj: "Skip setups whose projected RVOL is below this (0 = off).",
    require_fresh_extreme: "1 = only enter while the stock is still making new session lows (BEAR) / highs (BULL).",
    fresh_extreme_bars: "The session low/high must have been made within this many completed 15m bars.",
    max_dist_from_extreme_pct: "…and the last 15m close must be within this % of that low/high.",
    fresh_extreme_mode: "Momentum OI: the 'new low/high within the last N bars' part of the fresh-extreme check. 'log' (default since 2026-10-19) only records a miss on the trade (fresh_extreme.not_fresh_would_block); 'block' skips the entry as before. The distance check above (too far from the low/high) still blocks either way. Replayed skips 5–19 Oct: not fresh 7W/4L, too far 3W/11L.",
    require_index_alignment: "1 = skip when the last completed Nifty 15m bar moved against the trade direction.",
    max_losses_per_day: "Stop NEW entries (paper and live) after this many losing trades today. 0 = off.",
    broker_sl_enabled: "Live trades only: 1 = place an exchange stop-loss (SL-L) SELL order at Upstox right after the BUY fills, so the stop is enforced tick by tick.",
    broker_sl_limit_buffer_pct: "Limit price of the broker SL order = trigger − this %, so a fast gap still fills.",
    premium_gates_mode: "Premium chase/fade entry gates. log = record would-block on each trade only (default); block = skip the entry; off = don't evaluate.",
    max_premium_move_from_open_pct: "Premium-chase gate: option premium already up this % or more from today's open.",
    max_premium_fade_pct: "Premium-fade gate: fresh premium more than this % below the last closed 1-minute bar.",
    max_adverse_move_pct: "Momentum OI: skip the entry (paper and live) if the stock has already moved more than this % AGAINST the trade since the last closed 15-min bar — up for a PE trade, down for a CE trade. Default 0.25 (MAXHEALTH 30-Sep was 0.31% against at entry, then -16.5%; no winner was above 0.13%). 0 = off.",
    premium_noise_mode: "Momentum OI: how the noise check (max_premium_noise_pct) is used. 'log' (default since 2026-10-26) only records it on the trade (entry_checks.premium_noise.would_block); 'block' skips the entry; 'off' = not evaluated. Changed after its first live week: replayed skips 19–23 Oct went 4W/3L.",
    late_entry_mode: "Momentum OI: skip the entry when Nifty has already moved 0.2% or more vs yesterday's close in the trade's direction (the Journal's late-entry flag — down for a PE, up for a CE). 'block' (default) skips it, and the skipped pick is still replayed in the outcome tracker (reason late_entry_nifty_moved); 'log' only records it; 'off'. Since Oct: late entries 17 trades 5W, −₹13.8k; the rest 9 trades 5W, +₹2.8k. Unknown Nifty data never blocks. Added 2026-10-26.",
    max_premium_noise_pct: "Momentum OI: skip the entry (paper and live) when the option's average 1-minute high-low range over the last few closed minutes is above this % of its price — the 7% stop would sit inside normal noise. 30-Sep to 16-Oct: ≤4.5% 5W/2L PF 2.55, >4.5% 3W/5L PF 0.51 (15 trades). Default 4.5. 0 = off. Added 2026-10-16.",
    shadow_gates_mode: "Momentum OI: 'log' (default) = the option-liquidity and stock-move checks below are only recorded on each trade (entry_checks.shadow_gates), never block; 'block' = skip the entry; 'off' = not evaluated. Added 2026-10-16 for a one-week review.",
    min_option_lots_5m: "Momentum OI (shadow gate): the option must have traded at least this many lots over the last few closed 1-minute bars, and its LTP must sit inside the bid/ask (else it's a stale print). KAYNES 08-Oct had ~1 lot in 5 min and an entry below the bid. Default 10.",
    max_stock_move_pct: "Momentum OI (shadow gate): flag entries where the stock is already more than this % up or down on the day (exhaustion). 15 fixed-exit trades: >5% went 1W/3L (TECHM +7.55% on 16-Oct). Default 5.",
    premium_noise_bars: "Momentum OI: how many closed 1-minute option bars the noise check averages (default 5). Fewer than 3 bars available = never blocks.",
  },
};

const ACRONYMS = new Set([
  "vwap", "rsi", "atr", "eod", "ist", "rvol", "adx", "oi", "hh", "mm",
  "sl", "tp", "rr", "ema", "ce", "pe", "htf", "v2", "v3", "v4", "id", "r",
]);

// Fallback for any key without a curated label above — turns
// `setup_c_v2_score_two_reds` into `Setup C V2 Score Two Reds`.
const prettyLabel = (k) =>
  k.split("_").map((w) => (ACRONYMS.has(w.toLowerCase()) ? w.toUpperCase() : w.charAt(0).toUpperCase() + w.slice(1)))
    .join(" ");

// Section 9's `markets` value is a nested object (per-market thresholds),
// not a scalar/list — the generic number/string/list inputs below can't
// edit it safely, so render it as a read-only summary instead.
const MarketsSummary = ({ markets }) => (
  <div className="border border-grid p-3 space-y-3" data-testid="admin-config-markets-summary">
    <div className="text-[10px] text-muted-3 font-mono-data leading-relaxed">
      Per-market thresholds for Setup E. Read-only here — these are the engine defaults
      (or saved overrides) for score, volatility, and timing gates per market.
    </div>
    {Object.entries(markets || {}).map(([mkt, cfg]) => (
      <div key={mkt} className="text-[11px] font-mono-data">
        <div className="text-muted-1 mb-1">{mkt}</div>
        <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-muted-3 pl-2">
          <div>Score threshold: {cfg.score_threshold}</div>
          <div>Premium expansion: {cfg.premium_expansion_pct}%</div>
          <div>RVOL: {cfg.rvol_threshold}</div>
          <div>Velocity: {cfg.velocity_threshold}</div>
          <div>Scan: {String(cfg.scan_start?.[0]).padStart(2, "0")}:{String(cfg.scan_start?.[1]).padStart(2, "0")}
            –{String(cfg.scan_end?.[0]).padStart(2, "0")}:{String(cfg.scan_end?.[1]).padStart(2, "0")}</div>
          <div>Hard exit: {String(cfg.hard_exit?.[0]).padStart(2, "0")}:{String(cfg.hard_exit?.[1]).padStart(2, "0")}</div>
          <div>Strike interval: {cfg.strike_interval}</div>
          <div>Expiry: {cfg.expiry_cadence === "monthly_last_dow" ? "monthly (last)" : "weekly"}, DOW {cfg.expiry_dow}</div>
          <div>Lot size: {cfg.lot_size_hint}</div>
        </div>
      </div>
    ))}
  </div>
);


export const AdminConfiguration = () => {
  const [section, setSection] = useState("section5");
  const [cfg, setCfg] = useState(null);
  const [editing, setEditing] = useState({});
  const [fieldFilter, setFieldFilter] = useState("");

  const load = useCallback(async () => {
    const { data } = await api.get(`/admin/config/${section}`);
    setCfg(data);
    setEditing(data.values || {});
  }, [section]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    // List-typed fields (Array in defaults) may be held as raw comma-separated
    // strings while the user is typing (fixes the "can't type comma" bug).
    // Split them into arrays only at save time. Numeric fields are held as
    // raw strings the same way while typing (see the input's onChange below)
    // — parse to a number only here.
    const values = {};
    Object.entries(cfg?.defaults || {}).forEach(([k, defaultVal]) => {
      const v = editing[k];
      if (Array.isArray(defaultVal)) {
        if (typeof v === "string") {
          values[k] = v.split(",").map((s) => s.trim()).filter(Boolean);
        } else {
          values[k] = Array.isArray(v) ? v : (defaultVal || []);
        }
      } else if (typeof defaultVal === "boolean") {
        // 2026-10-03 — booleans (e.g. kite_execution.auto_confirm) are saved
        // as real true/false; typed text like "0"/"false" used to read as ON.
        values[k] = v === true || ["1", "true", "yes", "on"].includes(String(v ?? "").trim().toLowerCase());
      } else if (typeof defaultVal === "number") {
        const parsed = typeof v === "string" ? parseFloat(v) : v;
        values[k] = (v === undefined || v === "" || Number.isNaN(parsed)) ? defaultVal : parsed;
      } else {
        values[k] = v ?? defaultVal;
      }
    });
    const { data } = await api.post(`/admin/config/${section}`, {
      values,
      enabled: cfg?.enabled ?? true,
    });
    setCfg({ ...cfg, ...data });
    toast.success(`${section} config saved`);
  };
  const reset = async () => {
    if (!window.confirm(`Reset ${section} to default values?`)) return;
    const { data } = await api.post(`/admin/config/${section}/reset`);
    setCfg({ ...cfg, values: data.values, enabled: true });
    setEditing(data.values);
    toast.success("Reset to defaults");
  };

  return (
    <section className="space-y-4" data-testid="admin-config-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-muted-3 font-mono-data mb-1">
            Admin · Manage Configuration
          </div>
          <h2 className="font-heading text-2xl flex items-center gap-2"><UserCog size={20} /> Section Filters</h2>
        </div>
        <select
          value={section}
          onChange={(e) => setSection(e.target.value)}
          data-testid="admin-config-section"
          className="bg-transparent border border-grid px-3 py-1.5 text-xs"
        >
          <option value="section4">Section 4 — Sniper Setups</option>
          <option value="section5">Section 5 — Candle Breakout</option>
          <option value="section6">Section 6 — OI Long Buildup</option>
          <option value="section7">Section 7 — Live Orders</option>
          <option value="section8">Section 8 — VWAP Bounce</option>
          <option value="section9">Section 9 — Setup E V2 · Expiry Momentum</option>
          <option value="section10">Section 10 — VWAP + OI Smart Money</option>
          <option value="bearish_pe">Section 12 — Bearish PE Setup V1</option>
          <option value="momentum_oi">Momentum OI</option>
          <option value="kite_execution">Section 13 — Kite Execution (Crude Oil)</option>
          <option value="risk_governance">Risk Governance — daily loss limit &amp; cross-setup gates</option>
          <option value="section_steady_intraday_trend">Section 14 — Steady Intraday Trend</option>
          <option value="section_linear_accumulation">Section 14 — Linear Accumulation (legacy)</option>
          <option value="section_options">Section — Option Liquidity (shared)</option>
          <option value="system">System — Upstox Sync</option>
        </select>
      </div>

      {!cfg ? (
        <Skeleton className="h-48 w-full" />
      ) : (
        <div className="border border-grid p-5 space-y-4">
          <div className="flex justify-between items-center">
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-3 font-mono-data">
              Last updated:{" "}
              {cfg.updated_at
                ? new Date(cfg.updated_at).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })
                : "never (using defaults)"}
            </div>
            <label className="text-[10px] uppercase tracking-[0.2em] font-mono-data flex items-center gap-2">
              <input
                type="checkbox"
                checked={cfg.enabled}
                onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })}
                data-testid="admin-config-enabled"
              />
              Filters enabled
            </label>
          </div>
          <input
            type="text"
            value={fieldFilter}
            onChange={(e) => setFieldFilter(e.target.value)}
            placeholder="Search fields in this section (e.g. rsi, buy buffer, exit time)…"
            data-testid="admin-config-search"
            className="w-full bg-transparent border border-grid px-3 py-2 text-xs font-mono-data"
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {Object.entries(cfg.defaults || {}).filter(([k]) => {
              if (!fieldFilter.trim()) return true;
              const q = fieldFilter.trim().toLowerCase();
              const help = CONFIG_HELP_BY_SECTION[section]?.[k] ?? CONFIG_HELP[k] ?? "";
              return k.toLowerCase().includes(q) || prettyLabel(k).toLowerCase().includes(q) || help.toLowerCase().includes(q);
            }).map(([k, defaultVal]) => {
              const isString = typeof defaultVal === "string";
              const isList = Array.isArray(defaultVal);
              const isObject = !isList && defaultVal !== null && typeof defaultVal === "object";
              const currentVal = editing[k] ?? defaultVal;
              const help = CONFIG_HELP_BY_SECTION[section]?.[k] ?? CONFIG_HELP[k];

              if (isObject) {
                // e.g. section9's `markets` — nested per-market thresholds,
                // not editable via a single generic input. Show read-only.
                return (
                  <div key={k} className="space-y-1 md:col-span-2">
                    <label className="text-[10px] uppercase tracking-[0.2em] text-muted-3 font-mono-data">
                      {prettyLabel(k)}
                    </label>
                    {k === "markets" ? <MarketsSummary markets={defaultVal} /> : null}
                  </div>
                );
              }

              return (
                <div key={k} className="space-y-1">
                  <label className="text-xs font-heading" title={k}>
                    {prettyLabel(k)}
                  </label>
                  <div className="text-[9px] uppercase tracking-[0.15em] text-muted-3 font-mono-data">
                    {k}{" "}
                    <span className="normal-case">
                      (default {isList ? `[${defaultVal.join(", ")}]` : isString ? `"${defaultVal}"` : String(defaultVal)})
                    </span>
                  </div>
                  {help && (
                    <div className="text-[11px] text-muted-2 leading-snug">{help}</div>
                  )}
                  {isList ? (
                    <input
                      type="text"
                      value={typeof currentVal === "string"
                        ? currentVal
                        : (Array.isArray(currentVal) ? currentVal.join(", ") : "")}
                      onChange={(e) => setEditing({
                        ...editing,
                        // Keep raw string while typing so commas/spaces work.
                        // Parsed into an array at save() time.
                        [k]: e.target.value,
                      })}
                      data-testid={`admin-config-${k}`}
                      className="w-full bg-transparent border border-grid px-3 py-2 text-sm font-mono-data"
                      placeholder="Comma-separated, e.g. HINDUNILVR, BHARTIARTL, TCS"
                    />
                  ) : typeof defaultVal === "boolean" ? (
                    <label className="flex items-center gap-2 text-sm font-mono-data">
                      <input
                        type="checkbox"
                        checked={currentVal === true || ["1", "true", "yes", "on"].includes(String(currentVal ?? "").trim().toLowerCase())}
                        onChange={(e) => setEditing({ ...editing, [k]: e.target.checked })}
                        data-testid={`admin-config-${k}`}
                      />
                      {(currentVal === true || ["1", "true", "yes", "on"].includes(String(currentVal ?? "").trim().toLowerCase())) ? "ON" : "OFF"}
                    </label>
                  ) : isString && CONFIG_ENUMS[section]?.[k] ? (
                    // 2026-09-28 — enum-valued string fields (Momentum OI v1.1
                    // F4 `live_entry_mode`) render as a dropdown, so a typo
                    // can't silently fall back to the default mode.
                    <select
                      value={currentVal}
                      onChange={(e) => setEditing({ ...editing, [k]: e.target.value })}
                      data-testid={`admin-config-${k}`}
                      className="w-full bg-transparent border border-grid px-3 py-2 text-sm font-mono-data"
                    >
                      {CONFIG_ENUMS[section][k].map((opt) => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                  ) : isString ? (
                    <input
                      type="text"
                      value={currentVal}
                      onChange={(e) => setEditing({ ...editing, [k]: e.target.value })}
                      data-testid={`admin-config-${k}`}
                      className="w-full bg-transparent border border-grid px-3 py-2 text-sm font-mono-data"
                      placeholder={
                        k === "upstox_order_proxy_url"
                          ? "e.g. http://tradesteadyalgo:PASSWORD@203.0.113.45:49155"
                          : k === "tradesteady_static_ip"
                          ? "e.g. 203.0.113.45"
                          : String(defaultVal || "")
                      }
                    />
                  ) : (
                    // 2026-09-16 — keep the raw string while typing instead of
                    // parseFloat-ing on every keystroke (chat: "unable to add
                    // minus (negative) 2....it seems only positives are
                    // allowed"). Typing just "-" produced parseFloat("-") =
                    // NaN, which got set as the controlled value and reset the
                    // field before a digit could follow — so a leading minus
                    // sign could never survive. Parsed to a number in save()
                    // instead, same "hold the raw string, parse at save"
                    // pattern the comma-separated list fields above already use.
                    <input
                      type="number"
                      step="0.1"
                      value={currentVal}
                      onChange={(e) => setEditing({ ...editing, [k]: e.target.value })}
                      data-testid={`admin-config-${k}`}
                      className="w-full bg-transparent border border-grid px-3 py-2 text-sm font-mono-data"
                    />
                  )}
                  {section === "system" && k === "upstox_order_proxy_url" && (
                    <div
                      className="text-[10px] text-muted-3 font-mono-data mt-1 leading-relaxed"
                      data-testid="admin-config-upstox-proxy-help"
                    >
                      Full TradeSteady Algo proxy URL (scheme, credentials, port).
                      Set this once — when TradeSteady's whitelisted IP changes,
                      update the <span className="text-muted-1">TradeSteady Static IP</span> field
                      below instead of retyping this whole URL.<br />
                      Leave empty to fall back to the direct Upstox v2 endpoint.
                    </div>
                  )}
                  {section === "system" && k === "tradesteady_static_ip" && (
                    <>
                      <div
                        className="text-[10px] text-muted-3 font-mono-data mt-1 leading-relaxed"
                        data-testid="admin-config-tradesteady-ip-help"
                      >
                        Just the IP address. It's substituted into the URL above
                        automatically at order-placement time — update this whenever
                        TradeSteady Algo's static IP expires or changes.
                        Leave empty to use the URL above exactly as saved.
                      </div>
                      <UpstoxProxyTester
                        template={editing.upstox_order_proxy_url ?? cfg.defaults.upstox_order_proxy_url}
                        ip={currentVal}
                      />
                    </>
                  )}
                </div>
              );
            })}
          </div>
          <div className="flex gap-3">
            <button
              onClick={save}
              data-testid="admin-config-save"
              className="px-4 py-2 border text-[11px] uppercase tracking-[0.2em] font-mono-data"
              style={{ color: "var(--bull)", borderColor: "var(--bull)" }}
            >
              Save
            </button>
            <button
              onClick={reset}
              data-testid="admin-config-reset"
              className="px-4 py-2 border border-grid text-[11px] uppercase tracking-[0.2em] font-mono-data"
            >
              Reset to defaults
            </button>
          </div>
        </div>
      )}
    </section>
  );
};


export const AdminAlerts = () => {
  const [users, setUsers] = useState([]);
  const [savingKey, setSavingKey] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get("/admin/users");
    setUsers(data.users || []);
  }, []);

  useEffect(() => { load(); }, [load]);

  const SECTIONS = [
    { key: "section5", label: "S5 · Candle Breakout" },
    { key: "section6", label: "S6 · OI Long Buildup" },
    { key: "section7", label: "S7 · Live Orders" },
    { key: "section8", label: "S8 · VWAP Bounce" },
  ];
  const FLAGS = ["paper", "live", "telegram"];

  const flagFor = (u, sec, flag) => {
    const cur = u.alerts?.[sec] || { paper: true, live: true, telegram: true };
    return cur[flag] !== false;
  };

  const toggle = async (u, sec, flag) => {
    const cur = u.alerts?.[sec] || { paper: true, live: true, telegram: true };
    const next = { ...cur, [flag]: !flagFor(u, sec, flag) };
    const key = `${u.user_id}-${sec}-${flag}`;
    setSavingKey(key);
    try {
      await api.post(`/admin/users/${u.user_id}/alerts`, { section: sec, ...next });
      // Optimistic local update
      setUsers((prev) => prev.map((row) =>
        row.user_id === u.user_id
          ? { ...row, alerts: { ...(row.alerts || {}), [sec]: next } }
          : row,
      ));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally {
      setSavingKey(null);
    }
  };

  return (
    <section className="space-y-4" data-testid="admin-alerts-page">
      <div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-muted-3 font-mono-data mb-1">
          Admin · Manage Alerts
        </div>
        <h2 className="font-heading text-2xl flex items-center gap-2"><ShieldCheck size={20} /> Per-User Alert Matrix</h2>
        <p className="text-[11px] text-muted-2 mt-2 max-w-2xl">
          Each cell flips the user&apos;s opt-in for that section&apos;s alert channel. Changes
          save immediately. Paper = scan picks, Live = real broker orders,
          Telegram = forward to that user&apos;s personal chat (set under their Settings).
        </p>
      </div>

      <div className="border border-grid overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-grid text-[10px] uppercase tracking-[0.15em] text-muted-3 font-mono-data">
              <th rowSpan={2} className="px-3 py-2 text-left align-bottom sticky left-0 bg-black/60 z-10">
                User
              </th>
              <th rowSpan={2} className="px-3 py-2 text-left align-bottom">Role</th>
              <th rowSpan={2} className="px-3 py-2 text-left align-bottom">Sub</th>
              {SECTIONS.map((s) => (
                <th key={s.key} colSpan={FLAGS.length} className="px-2 py-2 text-center border-l border-grid">
                  {s.label}
                </th>
              ))}
            </tr>
            <tr className="border-b border-grid text-[9px] uppercase tracking-[0.15em] text-muted-3 font-mono-data">
              {SECTIONS.map((s) => (
                FLAGS.map((f, i) => (
                  <th key={`${s.key}-${f}`}
                      className={`px-2 py-1 text-center ${i === 0 ? "border-l border-grid" : ""}`}>
                    {f}
                  </th>
                ))
              ))}
            </tr>
          </thead>
          <tbody>
            {users.map((u, i) => (
              <tr key={u.user_id}
                  className="border-b border-grid hover:bg-white/[0.02]"
                  data-testid={`admin-alert-row-${i}`}>
                <td className="px-3 py-2 sticky left-0 bg-black/40 z-10">
                  <div className="font-semibold">{u.name || u.email}</div>
                  <div className="text-[10px] text-muted-3 font-mono-data">{u.email}</div>
                </td>
                <td className="px-3 py-2 text-[10px] font-mono-data uppercase">
                  {u.role}
                </td>
                <td className="px-3 py-2 text-[10px] font-mono-data uppercase">
                  {u.subscription}
                </td>
                {SECTIONS.map((sec) =>
                  FLAGS.map((flag, idx) => {
                    const on = flagFor(u, sec.key, flag);
                    const key = `${u.user_id}-${sec.key}-${flag}`;
                    return (
                      <td key={key}
                          className={`px-2 py-2 text-center ${idx === 0 ? "border-l border-grid" : ""}`}>
                        <button
                          onClick={() => toggle(u, sec.key, flag)}
                          disabled={savingKey === key}
                          data-testid={`admin-alert-cell-${i}-${sec.key}-${flag}`}
                          className="w-7 h-7 inline-flex items-center justify-center border text-[10px] font-mono-data uppercase"
                          style={{
                            color: on ? "var(--bull)" : "var(--muted-3)",
                            borderColor: on ? "var(--bull)" : "var(--grid)",
                            opacity: savingKey === key ? 0.4 : 1,
                          }}
                          title={`${flag} alerts ${on ? "ON" : "OFF"} for ${sec.label}`}
                        >
                          {on ? "✓" : "·"}
                        </button>
                      </td>
                    );
                  })
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="text-[10px] text-muted-3 font-mono-data uppercase tracking-[0.15em]">
        {users.length} user{users.length === 1 ? "" : "s"} · {SECTIONS.length} sections × {FLAGS.length} channels
      </div>
    </section>
  );
};


export const AdminSubscriptions = () => {
  const [users, setUsers] = useState([]);
  const load = useCallback(async () => {
    const { data } = await api.get("/admin/users");
    setUsers(data.users || []);
  }, []);
  useEffect(() => { load(); }, [load]);

  const setSub = async (u, subscription) => {
    await api.post(`/admin/users/${u.user_id}/subscription`, { subscription });
    toast.success(`${u.email} → ${subscription}`);
    load();
  };

  return (
    <section className="space-y-4" data-testid="admin-subscriptions-page">
      <div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-muted-3 font-mono-data mb-1">
          Admin · Manage Subscriptions
        </div>
        <h2 className="font-heading text-2xl flex items-center gap-2"><Crown size={20} /> Subscriptions</h2>
      </div>
      <div className="border border-grid overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-grid text-[10px] uppercase tracking-[0.2em] text-muted-3 font-mono-data">
              <th className="px-3 py-2 text-left">User</th>
              <th className="px-3 py-2 text-left">Role</th>
              <th className="px-3 py-2 text-left">Status</th>
              <th className="px-3 py-2 text-left">Subscription</th>
              <th className="px-3 py-2 text-right">Quick Set</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u, i) => (
              <tr key={u.user_id} className="border-b border-grid"
                  data-testid={`admin-sub-row-${i}`}>
                <td className="px-3 py-2">{u.email}</td>
                <td className="px-3 py-2">{u.role}</td>
                <td className="px-3 py-2">{u.status}</td>
                <td className="px-3 py-2 font-mono-data">{u.subscription}</td>
                <td className="px-3 py-2 text-right space-x-1">
                  {["NONE", "TIPS", "AUTO_TRADING"].map((s) => (
                    <button
                      key={s}
                      onClick={() => setSub(u, s)}
                      data-testid={`admin-sub-set-${i}-${s}`}
                      className="text-[10px] uppercase tracking-wider px-2 py-1 border border-grid hover:bg-white/[0.04]"
                      style={{ color: u.subscription === s ? "var(--bull)" : "var(--text-muted)" }}
                    >
                      {s}
                    </button>
                  ))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
};